// Single source of truth for system enum → zh-TW display label mapping.
//
// Why this module exists:
//   - Frontend UI uses these labels (Users / BindingCodes / Submissions
//     pages, etc.) and there was already drift — Users page rendered
//     `tenant_admin` as「單位管理員」 while AI Usage page rendered it
//     as「管理員」. One map prevents future divergence.
//   - AI chat tool_result needs labels so the model doesn't echo raw
//     enum IDs (`tenant_admin`, `active`) to end users. The enrich()
//     walker below adds `<field>_zh` siblings to any JSON value whose
//     property name is in the field→map index. Tool executor wraps
//     every API response with enrich() before posting to /tool-result;
//     the AI sees the labelled version, picks the readable string
//     naturally without any per-tool prompt rule.
//
// Adding a new enum: extend the relevant map + (if the field name
// hasn't appeared before) add an entry to FIELD_LABEL_MAP. No code
// changes elsewhere needed — enrich() is fully data-driven.
//
// Adding a new field whose values reuse an existing map: just add the
// field name → map entry to FIELD_LABEL_MAP. E.g. if a future API
// returns `prev_role: "manager"`, add `prev_role: ROLE_LABELS` to
// FIELD_LABEL_MAP and enrich() picks it up.

export const ROLE_LABELS: Record<string, string> = {
  tenant_admin: "單位管理員",
  manager: "單位主管",
  auditor: "單位稽核員",
  site_supervisor: "案場督導",
  officer: "巡邏員",
  incident_reporter: "異常通報員",
  control_center_operator: "監控中心操作員",
  client_viewer: "客戶檢視員",
  super_admin: "平台管理員",
};

export const STATUS_LABELS: Record<string, string> = {
  active: "啟用",
  invited: "已邀請",
  revoked: "已撤銷",
  paused: "已暫停",
  disabled: "已停用",
  in_progress: "進行中",
  completed: "已完成",
  missed: "已過期未完成",
  skipped: "已略過",
  incident: "有異常",
  redeemed: "已兌換",
  expired: "已過期",
  archived: "已封存",
  draft: "草稿",
  pending: "待處理",
  success: "成功",
  failed: "失敗",
};

export const TARGET_KIND_LABELS: Record<string, string> = {
  user: "使用者",
  role: "角色",
  officer: "巡邏員",
  site: "案場",
  client: "客戶",
  location: "地點",
  route: "路線",
  task: "任務",
  task_assignment: "任務派發",
  patrol_session: "巡邏 session",
  patrol_event: "巡邏紀錄",
  submission: "提交",
  scan_session: "掃描 session",
  ai_thread: "AI 對話",
  binding_code: "邀請碼",
  device: "裝置",
  shift: "班次",
  schedule: "排班",
  building: "建物",
  floor: "樓層",
  map_marker: "地圖標記",
  layout_version: "平面圖版本",
  tenant: "單位",
  notification: "通知",
  report: "報表",
  audit_log: "稽核紀錄",
};

export const ACTOR_KIND_LABELS: Record<string, string> = {
  platform_user: "平台使用者",
  tenant_user: "單位使用者",
  officer: "巡邏員",
  system: "系統",
  line_user: "LINE 使用者",
};

/** Property name → which label map to use. enrich() consults this to
 *  decide when a sibling `<name>_zh` should be injected. Property
 *  names not listed here are left alone, even if their value happens
 *  to be a known enum string in some other context. */
const FIELD_LABEL_MAP: Record<string, Record<string, string>> = {
  role: ROLE_LABELS,
  role_id: ROLE_LABELS,
  current_role: ROLE_LABELS,
  prev_role: ROLE_LABELS,
  legacy_role: ROLE_LABELS,
  role_v2: ROLE_LABELS,
  status: STATUS_LABELS,
  state: STATUS_LABELS,
  target_kind: TARGET_KIND_LABELS,
  actor_kind: ACTOR_KIND_LABELS,
};

/** Walk a JSON value and, for any object property whose key matches
 *  FIELD_LABEL_MAP and whose string value matches an entry in the
 *  corresponding map, inject a sibling `<key>_zh` property with the
 *  display label.
 *
 *  Properties:
 *    - Idempotent: if `<key>_zh` already exists on the object, it's
 *      preserved (no overwrite).
 *    - Cycle-safe via WeakSet tracker.
 *    - Returns a structurally cloned tree; caller's input is untouched.
 *    - Non-object scalars (null / number / boolean / string at the
 *      root) pass through unchanged.
 *
 *  Performance: O(n) over the JSON tree's node count. Typical AI
 *  tool_result (list_users with 50 rows) walks in <1ms.
 *
 *  Lookups are case-sensitive — backend enums are snake_case and
 *  case-stable, so this is intentional. */
export function enrichLabels<T>(value: T): T {
  const seen = new WeakSet<object>();
  return walk(value, seen) as T;
}

function walk(v: unknown, seen: WeakSet<object>): unknown {
  if (v === null || typeof v !== "object") return v;
  if (seen.has(v as object)) return v;
  seen.add(v as object);

  if (Array.isArray(v)) {
    return v.map((x) => walk(x, seen));
  }

  const obj = v as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(obj)) {
    out[k] = walk(val, seen);
    const map = FIELD_LABEL_MAP[k];
    if (map && typeof val === "string") {
      const labelKey = `${k}_zh`;
      if (!(labelKey in obj)) {
        const label = map[val];
        if (label) out[labelKey] = label;
      }
    }
  }
  return out;
}

/** Convenience accessor for direct label lookups (UI components that
 *  don't go through enrich). Mirrors the previous frontend pattern
 *  `roleLabel(role)` → `ROLE_LABELS[role] ?? role`. */
export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function targetKindLabel(kind: string): string {
  return TARGET_KIND_LABELS[kind] ?? kind;
}

export function actorKindLabel(kind: string): string {
  return ACTOR_KIND_LABELS[kind] ?? kind;
}
